import React from "react";
import { useRouter } from "next/navigation";

import { Button } from "@/components/Button";
import { Intro } from "@/components/Intro";
import { Typography } from "@/components/Typography";

import { getTokenData, isUserLoggedIn } from "@/services/CookiesService/CookiesService";
import { amplitudeTrackEvent } from "@/services/AmplitudeService/AmplitudeService";
import { isSubscriptionActive, isUserSubscribed } from "@/services/UserService";
import { assignPlanToUser } from "@/services/PlanService/PlanService";

import { useToast } from "@/hooks/ui/useToast";

import { paths } from "@/utils/paths";

import { AMPLITUDE_EVENT_NAME } from "@/internals/constants/events";
import { ActionTypes } from "@/internals/constants/actionTypes";

import { setPendingActionCookie } from "@/actions/setPendingActionCookie";

import { IPlansManager } from "./types";
import { PendingAction } from "@/types/pendingActions";

export const PlansManager: React.FC<IPlansManager> = ({ data, currentPlan, userContext }) => {
  const { toast } = useToast();
  const router = useRouter();

  const [isLoading, setIsLoading] = React.useState(false);

  const isStatusActive = isUserSubscribed(userContext) && isSubscriptionActive(userContext);

  const updateUserContextWithSelectedPlan = () => {
    userContext.setUser({
      ...userContext.user,
      selectedSubscriptionId: currentPlan.id,
      subscription: {
        ...userContext.user.subscription,
        plan: currentPlan.data,
      },
    });
  };

  const onContinueToPayHandler = async () => {
    const tokenData = getTokenData();

    if (!isUserLoggedIn() || tokenData?.isTokenExpired) {
      const pendingAction: PendingAction = {
        type: ActionTypes.SELECT_PLAN,
        payload: {
          user: userContext.user,
          planId: currentPlan.id,
        },
        redirectTo: paths.payment(),
      };

      try {
        await setPendingActionCookie(pendingAction);
        updateUserContextWithSelectedPlan();

        amplitudeTrackEvent(AMPLITUDE_EVENT_NAME.view_landing_signup, {
          source: "pricing",
        });
        router.push(paths.signup());
      } catch (error) {
        console.error("Error setting pending action cookie: ", error);
        toast({ message: "genericError", type: "error" });
      }

      return;
    }

    updateUserContextWithSelectedPlan();
    onChangePlan();
  };

  const onChangePlan = async () => {
    try {
      setIsLoading(true);
      await assignPlanToUser({
        userContext,
        planId: currentPlan.id,
      });

      router.push(paths.payment());
    } catch (error) {
      toast({ message: "genericError", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const renderTitleContent = () => {
    if (isStatusActive) {
      return <h1 className="intro-banner__title">{data?.title}</h1>;
    }

    return (
      <div className="intro-banner__subtitle__wrapper">
        <Typography
          Tag="p"
          classname="intro-banner__subtitle">
          {!isUserLoggedIn() ? data?.subtitle_1 : data?.subtitle_2}
        </Typography>
        <div className="intro-banner__button">
          <Button
            disabled={isLoading}
            dataQA="pricing-continue-button"
            onClick={onContinueToPayHandler}
            icon={isLoading}
            loading={isLoading}
            label={isLoading ? "loading" : "Continue"}
            rounded={!!data?.ctaStyle}
          />
        </div>
      </div>
    );
  };

  return (
    <Intro className={`intro-banner pricing intro-banner--${data?.version}`}>
      <div className="intro-banner__wrapper">{renderTitleContent()}</div>
    </Intro>
  );
};
